import { ChangeDetectorRef, Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { finalize, switchMap, catchError } from 'rxjs/operators';
import { DocumentService } from '../../../services/document/document.service';
import { ParametreDocumentStateService } from '../../../services/parametre-document/parametre-document-state.service';
import { ParametreNavService } from '../../../services/parametre/parametre-nav.service';
import {
    BaseCalculPrixAchat,
    isTruthyTinyInt,
    normalizeBaseCalculPrixAchat,
    toTinyInt01
} from '../../../services/parametre-document/parametre-doc-api-normalize';
import { ToastrService } from 'ngx-toastr';
import { StationService } from '../../../services/station.service';
import { environment } from '../../../../environments/environment';
import {
    formatStationIdAsCode,
    getStaticEnterpriseCode,
} from '../../../services/enterprise-context';
import { LogicielCaisseParametreAssociationsService } from '../../../services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import { of } from 'rxjs';
import {
    PARAMETRE_TYPE_DOCUMENT_VENTE,
    parseModelesReferenceMap,
    REFERENCE_DOC_TYPE_MENU,
    resolveModeleReferenceForType,
} from '../../../services/parametre-document/parametre-document-type-ids';

export interface ReferenceModel {
    id: number;
    model_type: number;
    current_counter: number;
    prefix: string | null;
    description: string;
    format_template?: string | null;
    seq_padding?: number;
    station_id?: number | null;
    type_document_id?: number | null;
    created_at?: string;
    updated_at?: string;
}

export type RefPartKind =
    | 'text'
    | 'sep'
    | 'YYYY'
    | 'YY'
    | 'MM'
    | 'DD'
    | 'YYYY-MM-DD'
    | 'YYYY/MM/DD'
    | 'YYYYMMDD'
    | 'YY-MM-DD'
    | 'YYYY-MM'
    | 'YYYYMM'
    | 'MM-YYYY'
    | 'PREFIX'
    | 'STATION'
    | 'ETAB'
    | 'ENT'
    | 'SEQ';

export interface RefBuilderPart {
    kind: RefPartKind;
    value?: string;
    label?: string;
}

const DEBUG_PARAMETRE_DOCUMENT_REF = false;

@Component({
    selector: 'app-parametre-document',
    templateUrl: './parametre-document.component.html',
    styleUrls: ['./parametre-document.component.scss']
})
export class ParametreDocumentComponent implements OnInit {
    fileUrl: string | null = null;
    cachetForApi: string | null = null;
    isImage: boolean = false;
    isPdf: boolean = false;
    isDragging = false;
    activeModel: number | null = null;

    loadingSettings = false;
    saving = false;
    private syncingLivraisonFromApi = false;
    savingActiveBonLiv = false;
    private syncingPrixAchatOptsFromApi = false;
    savingPrixAchatOpts = false;
    private syncingReferenceFromApi = false;
    savingReferenceModel = false;

    referenceModels: ReferenceModel[] = [];
    loadingReferenceModels = false;
    selectedRefModelId: number | null = null;
    /** Type de document actif dans le menu « Référence du document ». */
    selectedRefDocTypeId: number = PARAMETRE_TYPE_DOCUMENT_VENTE.facture;
    readonly refDocTypeMenu = REFERENCE_DOC_TYPE_MENU;
    /**
     * Modèle affiché en premier (figé à l’entrée page / changement d’onglet type).
     * Ne change pas quand l’utilisateur clique un autre modèle.
     */
    private refModelsPinnedFirstId: number | null = null;

    @ViewChild('refModelModal') refModelModalTpl?: TemplateRef<unknown>;
    @ViewChild('refModelsTrack') refModelsTrack?: ElementRef<HTMLElement>;
    private refModelModalRef: NgbModalRef | null = null;
    /** Pointer down en cours (souris) — drag seulement après seuil. */
    private refSwipePointerDown = false;
    private refSwipeDragging = false;
    private refSwipeDidDrag = false;
    private refSwipeStartX = 0;
    private refSwipeScrollLeft = 0;
    private refSwipePointerId: number | null = null;
    savingCustomRefModel = false;
    refBuilderParts: RefBuilderPart[] = [];
    refSeqPadding = 3;
    refFreeText = '';
    /** Séparateur inséré automatiquement entre deux variables ('' = collé). */
    refAutoSep: string = '-';
    refCustomSep = '';
    refAutoInsertSep = true;
    /** Choix rapide dans la barre : text | date | PREFIX | … */
    refPickType: '' | 'text' | 'date' | 'PREFIX' | 'STATION' | 'ETAB' | 'ENT' | 'SEQ' | 'sep' = '';
    refDateFormat: RefPartKind = 'YYYY-MM-DD';
    refManualSep = '-';
    /** Codes numériques pour aperçu {STATION}/{ETAB}/{ENT} (pas les noms). */
    previewStationCode = '001';
    previewEtabCode = '001';
    previewEntrepriseCode = '001';

    readonly refDateOptions: { value: RefPartKind; label: string }[] = [
        { value: 'YYYY', label: 'YYYY (2026)' },
        { value: 'YY', label: 'YY (26)' },
        { value: 'MM', label: 'MM (07)' },
        { value: 'DD', label: 'JJ (22)' },
        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
        { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
        { value: 'YYYYMMDD', label: 'YYYYMMDD' },
        { value: 'YY-MM-DD', label: 'YY-MM-DD' },
        { value: 'YYYY-MM', label: 'YYYY-MM' },
        { value: 'YYYYMM', label: 'YYYYMM' },
        { value: 'MM-YYYY', label: 'MM-YYYY' },
    ];

    selectedDocModel: number = 1;

    selectedLangue: 'francais' | 'anglais' | 'arabe' = 'francais';

    selectedLivraison: 'oui' | 'non' = 'non';

    /** Mettre à jour le prix d'achat sur la fiche produit depuis le document d'achat. */
    selectedMajPrixAchatProduit: 'oui' | 'non' = 'non';

    /** Prix unitaire d'entrée pour le CMUP (facture fournisseur). */
    selectedBaseCalculPrixAchat: BaseCalculPrixAchat = 'document_achat';

    selectedDocType: 'achat' | 'vente' | null = null;

    get stationId(): number {
        return this.state.getStationId();
    }

    constructor(
        private router: Router,
        private documentService: DocumentService,
        private state: ParametreDocumentStateService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
        private stationService: StationService,
        private readonly parametreNav: ParametreNavService,
        private readonly modalService: NgbModal,
        private readonly lcAssociations: LogicielCaisseParametreAssociationsService
    ) {}

    ngOnInit(): void {
        this.refreshPreviewCodes();
        this.loadReferenceModelsFromDocument();
        this.loadParametreDocumentSettings();
    }

    /** Codes station / établissement / entreprise pour l’aperçu (codes, jamais les libellés). */
    private refreshPreviewCodes(): void {
        this.previewStationCode = formatStationIdAsCode(this.stationId) || '001';
        this.previewEntrepriseCode = getStaticEnterpriseCode() || this.previewStationCode || '001';
        this.previewEtabCode = '001';
        this.lcAssociations
            .listEtablissements()
            .pipe(catchError(() => of([])))
            .subscribe((rows) => {
                const code = String(rows?.[0]?.code_etablissement || '').trim();
                if (code) {
                    this.previewEtabCode = code;
                    this.cdr.markForCheck();
                }
            });
    }

    /** Débloque la barre de progression paramètres après chargement initial. */
    private notifyParametrePageReady(): void {
        this.parametreNav.markPageReady();
    }

    private loadReferenceModelsFromDocument(): void {
        this.loadingReferenceModels = true;
        this.documentService.getReferenceModelsAll().subscribe({
            next: (response: any) => {
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.log('[ParametreDocument] GET document (références) — réponse brute:', response);
                }
                const data = response?.data;
                const list = Array.isArray(data) ? data : [];
                this.referenceModels = list
                    .map((m: ReferenceModel) => {
                        const id = this.normalizeRefModelId(m.id);
                        return id != null ? { ...m, id } : null;
                    })
                    .filter((m): m is ReferenceModel => m != null);
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.log(
                        '[ParametreDocument] GET document — modèles mappés (ids):',
                        this.referenceModels.map((m) => ({ id: m.id, description: m.description }))
                    );
                }
                this.loadingReferenceModels = false;
                this.applyReferenceRadioFromParametre('après GET document');
                this.notifyParametrePageReady();
            },
            error: (err) => {
                if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                    console.error('[ParametreDocument] GET document — erreur:', err);
                }
                this.referenceModels = [];
                this.loadingReferenceModels = false;
                this.notifyParametrePageReady();
            }
        });
    }

    private loadParametreDocumentSettings(): void {
        if (this.stationId === 0) {
            this.notifyParametrePageReady();
            return;
        }
        this.loadingSettings = true;
        this.state
            .ensureLoaded()
            .pipe(finalize(() => {
                this.loadingSettings = false;
                this.notifyParametrePageReady();
            }))
            .subscribe({
                next: () => {
                    if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                        console.log('[ParametreDocument] GET paramètres — ensureLoaded terminé');
                        this.logStationSnapshot('avant applyMainFormFromState');
                    }
                    this.applyMainFormFromState();
                    this.applyReferenceRadioFromParametre('après GET paramètres');
                },
                error: (err) => {
                    if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                        console.error('[ParametreDocument] GET paramètres — erreur:', err);
                    }
                    this.toastr.error('Impossible de charger les paramètres document.', 'Erreur');
                }
            });
    }

    private applyMainFormFromState(): void {
        const s = this.state.station;
        this.selectedDocModel = Number(s.modele) === 2 ? 2 : 1;
        this.selectedLangue = (s.langue as 'francais' | 'anglais' | 'arabe') ?? 'francais';
        this.syncingLivraisonFromApi = true;
        this.selectedLivraison = isTruthyTinyInt(s.active_bon_liv) ? 'oui' : 'non';
        this.syncActiveBonLivToState();
        setTimeout(() => {
            this.syncingLivraisonFromApi = false;
        }, 0);
        this.syncingPrixAchatOptsFromApi = true;
        this.selectedMajPrixAchatProduit = isTruthyTinyInt(s.maj_prix_achat_produit) ? 'oui' : 'non';
        this.selectedBaseCalculPrixAchat = normalizeBaseCalculPrixAchat(s.base_calcul_prix_achat);
        this.syncPrixAchatOptsToState();
        setTimeout(() => {
            this.syncingPrixAchatOptsFromApi = false;
        }, 0);
        this.cachetForApi = s.image_cachet ?? null;
        this.applyImagePreviewFromCachet(s.image_cachet ?? null);

        this.selectedDocType = s.document_category === 'achat' ? 'achat' : null;
        if (DEBUG_PARAMETRE_DOCUMENT_REF) {
            this.logStationSnapshot('fin applyMainFormFromState');
        }
    }

    private logStationSnapshot(label: string): void {
        if (!DEBUG_PARAMETRE_DOCUMENT_REF) {
            return;
        }
        const st = this.state.station as Record<string, unknown>;
        console.log(`[ParametreDocument] station (${label}):`, {
            modele_reference: st['modele_reference'],
            modele_reference_id: st['modele_reference_id'],
            reference_model_id: st['reference_model_id'],
            parametre_document: st['parametre_document'],
            keys: st != null ? Object.keys(st) : []
        });
    }

    private normalizeRefModelId(raw: unknown): number | null {
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
    }

    private resolveReferenceIdForCards(stored: number | null): number | null {
        if (stored == null) {
            return null;
        }
        const n = Number(stored);
        if (Number.isNaN(n)) {
            return null;
        }
        const models = this.referenceModelsForSelectedType;
        if (models.length === 0) {
            return n;
        }
        if (models.some((m) => Number(m.id) === n)) {
            return n;
        }
        const byType = models.find((m) => Number(m.model_type) === n);
        return byType != null ? Number(byType.id) : n;
    }

    private getModeleReferenceRaw(station: Record<string, unknown> | null | undefined): unknown {
        if (station == null) {
            return undefined;
        }
        const nested = station['parametre_document'];
        const pd = nested != null && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
        return (
            station['modele_reference'] ??
            station['modele_reference_id'] ??
            station['reference_model_id'] ??
            station['id_modele_reference'] ??
            pd?.['modele_reference'] ??
            pd?.['reference_model_id']
        );
    }

    private applyReferenceRadioFromParametre(from?: string): void {
        const loaded = this.state.isParametreDocumentLoaded();
        if (DEBUG_PARAMETRE_DOCUMENT_REF) {
            console.log(`[ParametreDocument] applyReferenceRadioFromParametre — déclenché ${from ? `(${from})` : ''}`, {
                isParametreDocumentLoaded: loaded,
                referenceModelsCount: this.referenceModels.length
            });
        }
        if (!loaded) {
            if (DEBUG_PARAMETRE_DOCUMENT_REF) {
                console.warn(
                    '[ParametreDocument] applyReferenceRadioFromParametre — SKIP: paramètres pas encore chargés (ensureLoaded pas fini). Réessai au prochain appel.'
                );
            }
            return;
        }
        const raw = resolveModeleReferenceForType(
            this.state.station?.modeles_reference,
            this.selectedRefDocTypeId,
            this.normalizeRefModelId(this.getModeleReferenceRaw(this.state.station as Record<string, unknown>))
        );
        const normalized = this.normalizeRefModelId(raw);
        const id = this.resolveReferenceIdForCards(normalized);
        this.syncingReferenceFromApi = true;
        this.selectedRefModelId = id;
        // En tête uniquement à l’entrée / rechargement / changement d’onglet — pas au clic
        this.refModelsPinnedFirstId = id;
        this.syncModeleReferenceToState();
        this.onReferenceModelChange();
        setTimeout(() => {
            this.syncingReferenceFromApi = false;
            this.cdr.markForCheck();
        }, 0);
        this.cdr.markForCheck();
        setTimeout(() => this.cdr.detectChanges(), 0);
    }

    /** Modèles visibles pour le type sélectionné (spécifiques + partagés).
     * Le modèle enregistré est en premier seulement via refModelsPinnedFirstId
     * (entrée page / onglet), pas au moment du choix. */
    get referenceModelsForSelectedType(): ReferenceModel[] {
        const tid = Number(this.selectedRefDocTypeId);
        const list = this.referenceModels.filter((m) => {
            const mt = m.type_document_id != null ? Number(m.type_document_id) : null;
            return mt == null || mt === tid;
        });
        const pinnedId =
            this.refModelsPinnedFirstId != null ? Number(this.refModelsPinnedFirstId) : null;
        if (pinnedId == null || !Number.isFinite(pinnedId)) {
            return list;
        }
        return [...list].sort((a, b) => {
            const aPin = Number(a.id) === pinnedId ? 0 : 1;
            const bPin = Number(b.id) === pinnedId ? 0 : 1;
            if (aPin !== bPin) {
                return aPin - bPin;
            }
            return Number(a.model_type) - Number(b.model_type);
        });
    }

    onRefModelsPointerDown(event: PointerEvent): void {
        const el = this.refModelsTrack?.nativeElement;
        if (!el || event.pointerType === 'touch') {
            return;
        }
        this.refSwipePointerDown = true;
        this.refSwipeDragging = false;
        this.refSwipeDidDrag = false;
        this.refSwipeStartX = event.clientX;
        this.refSwipeScrollLeft = el.scrollLeft;
        this.refSwipePointerId = event.pointerId;
    }

    onRefModelsPointerMove(event: PointerEvent): void {
        if (!this.refSwipePointerDown) {
            return;
        }
        const el = this.refModelsTrack?.nativeElement;
        if (!el) {
            return;
        }
        const dx = event.clientX - this.refSwipeStartX;
        if (!this.refSwipeDragging) {
            if (Math.abs(dx) <= 8) {
                return;
            }
            this.refSwipeDragging = true;
            this.refSwipeDidDrag = true;
            if (this.refSwipePointerId != null) {
                el.setPointerCapture?.(this.refSwipePointerId);
            }
        }
        el.scrollLeft = this.refSwipeScrollLeft - dx;
    }

    onRefModelsPointerUp(): void {
        const el = this.refModelsTrack?.nativeElement;
        if (el && this.refSwipePointerId != null && this.refSwipeDragging) {
            try {
                el.releasePointerCapture?.(this.refSwipePointerId);
            } catch {
                /* ignore */
            }
        }
        this.refSwipePointerDown = false;
        this.refSwipeDragging = false;
        this.refSwipePointerId = null;
        // refSwipeDidDrag reste true jusqu'au click suivant (ignoré)
    }

    selectReferenceModel(model: ReferenceModel, event?: Event): void {
        if (this.refSwipeDidDrag) {
            this.refSwipeDidDrag = false;
            event?.preventDefault();
            event?.stopPropagation();
            return;
        }
        if (this.savingReferenceModel || this.loadingSettings || model?.id == null) {
            return;
        }
        this.selectedRefModelId = Number(model.id);
        this.onReferenceModelChange();
        this.cdr.markForCheck();
    }

    get selectedRefDocTypeLabel(): string {
        return (
            this.refDocTypeMenu.find((t) => t.typeDocumentId === this.selectedRefDocTypeId)?.label ||
            'Document'
        );
    }

    get selectedRefDocTypeDefaultPrefix(): string {
        return (
            this.refDocTypeMenu.find((t) => t.typeDocumentId === this.selectedRefDocTypeId)
                ?.defaultPrefix || 'Fact'
        );
    }

    selectRefDocType(typeDocumentId: number): void {
        const tid = Number(typeDocumentId);
        if (!Number.isFinite(tid) || tid <= 0 || tid === this.selectedRefDocTypeId) {
            return;
        }
        this.selectedRefDocTypeId = tid;
        this.applyReferenceRadioFromParametre('changement type document');
        this.cdr.markForCheck();
        setTimeout(() => {
            const el = this.refModelsTrack?.nativeElement;
            if (el) {
                el.scrollTo({ left: 0, behavior: 'smooth' });
            }
        }, 0);
    }

    private syncModeleReferenceToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        const tid = Number(this.selectedRefDocTypeId);
        const map = parseModelesReferenceMap(this.state.station?.modeles_reference);
        if (this.selectedRefModelId != null && Number.isFinite(tid) && tid > 0) {
            map[String(tid)] = Number(this.selectedRefModelId);
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            modele_reference: this.selectedRefModelId,
            modeles_reference: map
        };
    }

    private resolveCachetDisplayUrl(raw: string): string {
        if (!raw) {
            return '';
        }
        if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
            return raw;
        }
        const base = environment.url_images.replace(/\/$/, '');
        const path = raw.startsWith('/') ? raw : `/${raw}`;
        return `${base}${path}`;
    }

    private applyImagePreviewFromCachet(raw: string | null): void {
        if (!raw) {
            this.fileUrl = null;
            this.isImage = false;
            this.isPdf = false;
            return;
        }
        this.fileUrl = this.resolveCachetDisplayUrl(raw);
        if (raw.startsWith('data:image')) {
            this.isImage = true;
            this.isPdf = false;
        } else if (raw.startsWith('data:application/pdf')) {
            this.isImage = false;
            this.isPdf = true;
        } else if (/\.pdf(\?|$)/i.test(raw)) {
            this.isImage = false;
            this.isPdf = true;
        } else {
            this.isImage = true;
            this.isPdf = false;
        }
    }

    onLivraisonChange(): void {
        if (this.stationId === 0) {
            this.syncActiveBonLivToState();
            return;
        }
        if (this.syncingLivraisonFromApi) {
            this.syncActiveBonLivToState();
            return;
        }
        const prev = toTinyInt01(this.state.station?.active_bon_liv);
        this.syncActiveBonLivToState();
        const next = toTinyInt01(this.selectedLivraison === 'oui' ? 1 : 0);
        if (next === prev) {
            return;
        }
        this.savingActiveBonLiv = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingActiveBonLiv = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Génération automatique du bon de livraison enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingLivraisonFromApi = true;
                            this.selectedLivraison = prev === 1 ? 'oui' : 'non';
                            this.syncActiveBonLivToState();
                            setTimeout(() => {
                                this.syncingLivraisonFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    private syncActiveBonLivToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            active_bon_liv: this.selectedLivraison === 'oui' ? 1 : 0
        };
    }

    private syncPrixAchatOptsToState(): void {
        const sid = this.stationId;
        if (sid === 0) {
            return;
        }
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            maj_prix_achat_produit: this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0,
            base_calcul_prix_achat: this.selectedBaseCalculPrixAchat
        };
    }

    onMajPrixAchatProduitChange(): void {
        if (this.stationId === 0) {
            this.syncPrixAchatOptsToState();
            return;
        }
        if (this.syncingPrixAchatOptsFromApi) {
            this.syncPrixAchatOptsToState();
            return;
        }
        const prev = toTinyInt01(this.state.station?.maj_prix_achat_produit);
        this.syncPrixAchatOptsToState();
        const next = toTinyInt01(this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0);
        if (next === prev) {
            return;
        }
        this.savingPrixAchatOpts = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingPrixAchatOpts = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Option « prix d’achat dans le produit » enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingPrixAchatOptsFromApi = true;
                            this.selectedMajPrixAchatProduit = prev === 1 ? 'oui' : 'non';
                            this.syncPrixAchatOptsToState();
                            setTimeout(() => {
                                this.syncingPrixAchatOptsFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    onBaseCalculPrixAchatChange(): void {
        if (this.stationId === 0) {
            this.syncPrixAchatOptsToState();
            return;
        }
        if (this.syncingPrixAchatOptsFromApi) {
            this.syncPrixAchatOptsToState();
            return;
        }
        const prev = normalizeBaseCalculPrixAchat(this.state.station?.base_calcul_prix_achat);
        this.syncPrixAchatOptsToState();
        const next = this.selectedBaseCalculPrixAchat;
        if (next === prev) {
            return;
        }
        this.savingPrixAchatOpts = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingPrixAchatOpts = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Base de calcul du prix d’achat enregistrée.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingPrixAchatOptsFromApi = true;
                            this.selectedBaseCalculPrixAchat = prev;
                            this.syncPrixAchatOptsToState();
                            setTimeout(() => {
                                this.syncingPrixAchatOptsFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    private syncStateStationFromForm(): void {
        const sid = this.stationId;
        this.state.station = {
            ...this.state.station,
            station_id: sid,
            modele: this.selectedDocModel === 2 ? 2 : 1,
            langue: this.selectedLangue,
            active_bon_liv: this.selectedLivraison === 'oui' ? 1 : 0,
            maj_prix_achat_produit: this.selectedMajPrixAchatProduit === 'oui' ? 1 : 0,
            base_calcul_prix_achat: this.selectedBaseCalculPrixAchat,
            image_cachet: this.cachetForApi,
            modele_reference: this.normalizeRefModelId(this.selectedRefModelId),
            document_category: this.selectedDocType ?? null
        };
    }

    saveParametreDocument(): void {
        if (this.stationId === 0) {
            this.toastr.warning('Station introuvable.', 'Enregistrement');
            return;
        }
        this.saving = true;
        this.syncStateStationFromForm();
        this.state
            .saveBundle()
            .pipe(finalize(() => (this.saving = false)))
            .subscribe({
                next: () => {
                    this.toastr.success('Paramètres document enregistrés.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                }
            });
    }

    private formatApiHttpError(err: unknown): string {
        const e = err as {
            status?: number;
            message?: string;
            error?: { message?: string; errors?: Record<string, string[] | string> };
        };
        if (e?.status === 0) {
            return 'Réseau indisponible ou CORS (vérifiez l’URL du service paramètres).';
        }
        const body = e?.error;
        if (body?.errors != null && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
            const lines: string[] = [];
            for (const val of Object.values(body.errors)) {
                if (Array.isArray(val)) {
                    val.forEach((s) => typeof s === 'string' && lines.push(s));
                } else if (typeof val === 'string') {
                    lines.push(val);
                }
            }
            if (lines.length > 0) {
                return lines.join(' ');
            }
        }
        const m = body?.message ?? e?.message;
        if (typeof m === 'string' && m.trim()) {
            return m;
        }
        if (e?.status != null && e.status >= 400) {
            return `Erreur HTTP ${e.status}`;
        }
        return 'Erreur serveur';
    }

    private static readonly REFERENCE_FORMAT_BY_TYPE: Record<number, string> = {
        1: 'FAC-00000X',
        2: 'FAC-2026-00000X',
        3: '2026-00000X',
        4: '202600000X',
        5: '00000X',
    };

    getReferenceFormat(model: ReferenceModel): string {
        if (model?.format_template) {
            return this.previewFromTemplate(model.format_template, model.seq_padding ?? 3, model.prefix);
        }
        const type = Number(model?.model_type);
        if (Number.isFinite(type) && ParametreDocumentComponent.REFERENCE_FORMAT_BY_TYPE[type]) {
            return ParametreDocumentComponent.REFERENCE_FORMAT_BY_TYPE[type];
        }
        if (!model?.description) {
            return '';
        }
        const idx = model.description.indexOf(':');
        return idx >= 0 ? model.description.slice(idx + 1).trim() : model.description;
    }

    openAddReferenceModelModal(): void {
        if (!this.refModelModalTpl) {
            return;
        }
        this.refreshPreviewCodes();
        this.refBuilderParts = [];
        this.refSeqPadding = 3;
        this.refFreeText = '';
        this.refAutoSep = '-';
        this.refCustomSep = '';
        this.refAutoInsertSep = true;
        this.refPickType = '';
        this.refDateFormat = 'YYYY-MM-DD';
        this.refManualSep = '-';
        const prefix = this.selectedRefDocTypeDefaultPrefix;
        this.pushRefPart({ kind: 'text', value: prefix, label: prefix });
        this.pushRefPart({ kind: 'YYYY', label: this.refPartLabel('YYYY') });
        this.pushRefPart({ kind: 'SEQ', label: this.refPartLabel('SEQ') });
        this.refModelModalRef = this.modalService.open(this.refModelModalTpl, {
            size: 'md',
            centered: true,
            backdrop: 'static',
            keyboard: false,
        });
    }

    closeRefModelModal(): void {
        this.refModelModalRef?.dismiss();
        this.refModelModalRef = null;
        this.refPickType = '';
        this.refFreeText = '';
    }

    onRefPickTypeChange(): void {
        this.refFreeText = '';
        if (
            this.refPickType === 'PREFIX' ||
            this.refPickType === 'STATION' ||
            this.refPickType === 'ETAB' ||
            this.refPickType === 'ENT' ||
            this.refPickType === 'SEQ'
        ) {
            this.addRefPart(this.refPickType);
            this.refPickType = '';
        }
    }

    confirmRefPick(): void {
        if (this.refPickType === 'text') {
            this.addRefPart('text');
            this.refPickType = '';
            return;
        }
        if (this.refPickType === 'date') {
            this.addRefPart(this.refDateFormat);
            this.refPickType = '';
            return;
        }
        if (this.refPickType === 'sep') {
            this.addSeparator(this.refManualSep);
            this.refPickType = '';
        }
    }

    get effectiveAutoSep(): string {
        if (this.refAutoSep === '__custom__') {
            return this.refCustomSep ?? '';
        }
        return this.refAutoSep;
    }

    private pushRefPart(part: RefBuilderPart): void {
        const next = [...this.refBuilderParts];
        if (
            this.refAutoInsertSep &&
            next.length > 0 &&
            part.kind !== 'sep' &&
            next[next.length - 1].kind !== 'sep'
        ) {
            const sep = this.effectiveAutoSep;
            // '' = collé volontairement, on n'ajoute rien
            if (sep !== '') {
                next.push({
                    kind: 'sep',
                    value: sep,
                    label: sep === ' ' ? 'espace' : sep,
                });
            }
        }
        next.push(part);
        this.refBuilderParts = next;
    }

    addRefPart(kind: RefPartKind): void {
        if (kind === 'text') {
            const v = (this.refFreeText || '').trim();
            if (!v) {
                this.toastr.warning('Saisissez un texte avant de l’ajouter.', 'Modèle');
                return;
            }
            this.pushRefPart({ kind: 'text', value: v, label: v });
            this.refFreeText = '';
            return;
        }
        if (kind === 'SEQ' && this.refBuilderParts.some((p) => p.kind === 'SEQ')) {
            this.toastr.info('Le compteur est déjà présent.', 'Modèle');
            return;
        }
        this.pushRefPart({ kind, label: this.refPartLabel(kind) });
    }

    addSeparator(value: string): void {
        const sep = value ?? '';
        this.refBuilderParts = [
            ...this.refBuilderParts,
            { kind: 'sep', value: sep, label: sep === ' ' ? 'espace' : sep || '∅' },
        ];
    }

    refPartLabel(kind: RefPartKind): string {
        const map: Partial<Record<RefPartKind, string>> = {
            YYYY: 'Année (YYYY)',
            YY: 'Année (YY)',
            MM: 'Mois',
            DD: 'Jour',
            'YYYY-MM-DD': 'Date YYYY-MM-DD',
            'YYYY/MM/DD': 'Date YYYY/MM/DD',
            YYYYMMDD: 'Date YYYYMMDD',
            'YY-MM-DD': 'Date YY-MM-DD',
            'YYYY-MM': 'Date YYYY-MM',
            YYYYMM: 'Date YYYYMM',
            'MM-YYYY': 'Date MM-YYYY',
            PREFIX: 'Préfixe doc',
            STATION: `Code station (${this.previewStationCode})`,
            ETAB: `Code étab. (${this.previewEtabCode})`,
            ENT: `Code entr. (${this.previewEntrepriseCode})`,
            SEQ: 'Compteur',
        };
        return map[kind] || kind;
    }

    removeRefPart(index: number): void {
        this.refBuilderParts = this.refBuilderParts.filter((_, i) => i !== index);
    }

    moveRefPart(index: number, dir: -1 | 1): void {
        const to = index + dir;
        if (to < 0 || to >= this.refBuilderParts.length) {
            return;
        }
        const next = [...this.refBuilderParts];
        const [item] = next.splice(index, 1);
        next.splice(to, 0, item);
        this.refBuilderParts = next;
    }

    get refBuilderTemplate(): string {
        return this.refBuilderParts
            .map((p) => {
                if (p.kind === 'text' || p.kind === 'sep') {
                    return String(p.value ?? '');
                }
                return `{${p.kind}}`;
            })
            .join('');
    }

    get refBuilderPreview(): string {
        return this.previewFromTemplate(this.refBuilderTemplate, this.refSeqPadding, null);
    }

    private previewFromTemplate(template: string, seqPadding: number, prefix: string | null | undefined): string {
        const pad = Math.min(8, Math.max(1, Number(seqPadding) || 3));
        const now = new Date();
        const yyyy = String(now.getFullYear());
        const yy = yyyy.slice(-2);
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const seq = '1'.padStart(pad, '0');
        return String(template || '')
            .replace(/\{YYYY-MM-DD\}/gi, `${yyyy}-${mm}-${dd}`)
            .replace(/\{YYYY\/MM\/DD\}/gi, `${yyyy}/${mm}/${dd}`)
            .replace(/\{YY-MM-DD\}/gi, `${yy}-${mm}-${dd}`)
            .replace(/\{YYYY-MM\}/gi, `${yyyy}-${mm}`)
            .replace(/\{MM-YYYY\}/gi, `${mm}-${yyyy}`)
            .replace(/\{YYYYMMDD\}/gi, `${yyyy}${mm}${dd}`)
            .replace(/\{YYMMDD\}/gi, `${yy}${mm}${dd}`)
            .replace(/\{YYYYMM\}/gi, `${yyyy}${mm}`)
            .replace(/\{PREFIX\}/gi, prefix || 'FAC')
            .replace(/\{YYYY\}/gi, yyyy)
            .replace(/\{YY\}/gi, yy)
            .replace(/\{MM\}/gi, mm)
            .replace(/\{DD\}/gi, dd)
            .replace(/\{SEQ\}/gi, seq)
            .replace(/\{STATION\}/gi, this.previewStationCode)
            .replace(/\{ETAB\}/gi, this.previewEtabCode)
            .replace(/\{ETABLISSEMENT\}/gi, this.previewEtabCode)
            .replace(/\{ENT\}/gi, this.previewEntrepriseCode)
            .replace(/\{ENTREPRISE\}/gi, this.previewEntrepriseCode);
    }

    saveCustomReferenceModel(): void {
        const template = this.refBuilderTemplate;
        if (!template || !/\{SEQ\}/i.test(template)) {
            this.toastr.warning('Ajoutez au moins le compteur (Compteur).', 'Modèle');
            return;
        }
        this.savingCustomRefModel = true;
        this.documentService
            .createReferenceModel({
                format_template: template,
                description: `Modèle ${this.selectedRefDocTypeLabel}: ${this.refBuilderPreview}`,
                seq_padding: this.refSeqPadding,
                station_id: this.stationId || null,
                type_document_id: this.selectedRefDocTypeId || null,
            })
            .pipe(finalize(() => (this.savingCustomRefModel = false)))
            .subscribe({
                next: (res) => {
                    const created = res?.data;
                    this.toastr.success(
                        `Modèle créé pour ${this.selectedRefDocTypeLabel}.`,
                        'Succès'
                    );
                    this.closeRefModelModal();
                    this.loadReferenceModelsFromDocument();
                    if (created?.id != null) {
                        this.selectedRefModelId = Number(created.id);
                        this.onReferenceModelChange();
                    }
                },
                error: (err) => {
                    this.toastr.error(
                        err?.error?.message || 'Création du modèle impossible.',
                        'Erreur'
                    );
                },
            });
    }

    getReferenceTitle(model: ReferenceModel): string {
        const type = Number(model?.model_type);
        if (Number.isFinite(type) && type > 0) {
            return `Mod\u00e8le ${type}`;
        }
        const id = Number(model?.id);
        return Number.isFinite(id) && id > 0 ? `Mod\u00e8le ${id}` : 'Mod\u00e8le';
    }

    isReferenceModelSelected(model: ReferenceModel): boolean {
        if (this.selectedRefModelId == null || model?.id == null) {
            return false;
        }
        return Number(this.selectedRefModelId) === Number(model.id);
    }

    onReferenceModelChange(): void {
        const sid = this.stationId;
        const prevCardId = this.resolveReferenceIdForCards(
            resolveModeleReferenceForType(
                this.state.station?.modeles_reference,
                this.selectedRefDocTypeId,
                this.normalizeRefModelId(this.state.station?.modele_reference)
            )
        );
        this.syncModeleReferenceToState();

        if (this.selectedRefModelId == null || sid === 0) {
            this.documentService.setDefaultReferenceModelType(
                sid,
                null,
                this.selectedRefDocTypeId
            );
            if (this.syncingReferenceFromApi || sid === 0) {
                return;
            }
            const nextCardId = this.normalizeRefModelId(this.selectedRefModelId);
            if (prevCardId === nextCardId) {
                return;
            }
            this.persistReferenceModelPut();
            return;
        }

        const model = this.referenceModels.find((m) => Number(m.id) === Number(this.selectedRefModelId));
        if (model != null) {
            this.documentService.setDefaultReferenceModelType(
                sid,
                model.model_type,
                this.selectedRefDocTypeId
            );
        }

        if (this.syncingReferenceFromApi) {
            return;
        }

        const nextCardId = this.normalizeRefModelId(this.selectedRefModelId);
        if (prevCardId === nextCardId) {
            return;
        }

        this.persistReferenceModelPut();
    }

    private persistReferenceModelPut(): void {
        if (this.stationId === 0) {
            return;
        }
        this.savingReferenceModel = true;
        this.state
            .ensureLoaded()
            .pipe(
                switchMap(() => this.state.saveBundle()),
                finalize(() => {
                    this.savingReferenceModel = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Modèle de référence enregistré.', 'Succès');
                },
                error: (err) => {
                    const msg = this.formatApiHttpError(err);
                    this.toastr.error(msg, 'Enregistrement');
                    this.state.reload().subscribe({
                        next: () => {
                            this.applyMainFormFromState();
                            this.applyReferenceRadioFromParametre('après erreur référence');
                            this.cdr.markForCheck();
                        },
                        error: () => {
                            this.syncingReferenceFromApi = true;
                            const raw = this.getModeleReferenceRaw(this.state.station as Record<string, unknown>);
                            const id = this.resolveReferenceIdForCards(this.normalizeRefModelId(raw));
                            this.selectedRefModelId = id;
                            this.syncModeleReferenceToState();
                            setTimeout(() => {
                                this.syncingReferenceFromApi = false;
                            }, 0);
                            this.cdr.markForCheck();
                        }
                    });
                }
            });
    }

    onDragOverModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = modelIndex;
    }

    onDragLeaveModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = null;
    }

    onDropModel(event: DragEvent, modelIndex: number) {
        event.preventDefault();
        this.activeModel = null;

        const file = event.dataTransfer?.files?.[0];
        if (file) this.handlePdfUpload(file, modelIndex);
    }

    onFileSelectedModel(event: Event, modelIndex: number) {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.handlePdfUpload(input.files[0], modelIndex);
        }
    }

    handlePdfUpload(file: File, modelIndex: number) {
        if (file.type !== 'application/pdf') {
            alert('Veuillez sélectionner un fichier PDF uniquement.');
            return;
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.handleFile(input.files[0]);
        }
    }

    triggerFileInput(fileInput: HTMLInputElement): void {
        fileInput?.click();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            this.handleFile(event.dataTransfer.files[0]);
        }
    }

    handleFile(file: File) {
        const fileType = file.type;
        if (!fileType.startsWith('image/') && fileType !== 'application/pdf') {
            this.isImage = false;
            this.isPdf = false;
            this.fileUrl = null;
            this.cachetForApi = null;
            this.toastr.warning('Veuillez choisir une image ou un fichier PDF.', 'Format');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        this.stationService.uploadFile(formData).subscribe({
            next: (res: { path?: string; url?: string; data?: { path?: string; url?: string } }) => {
                const body = (res as any)?.data ?? res;
                const path =
                    body?.path ??
                    (body?.url ? this.extractPathFromUploadUrl(body.url) : null);
                if (!path) {
                    this.toastr.error('Réponse serveur invalide : chemin du fichier manquant.', 'Cachet');
                    return;
                }
                this.cachetForApi = path;
                if (fileType.startsWith('image/')) {
                    this.isImage = true;
                    this.isPdf = false;
                } else {
                    this.isImage = false;
                    this.isPdf = true;
                }
                this.fileUrl = this.resolveCachetDisplayUrl(path);
                this.cdr.markForCheck();
            },
            error: () => {
                this.toastr.error('Échec du téléversement du cachet. Réessayez.', 'Cachet');
            }
        });
    }

    private extractPathFromUploadUrl(fullUrl: string): string | null {
        try {
            const base = environment.url_images.replace(/\/$/, '');
            if (fullUrl.startsWith(base)) {
                return fullUrl.slice(base.length) || null;
            }
            const u = new URL(fullUrl);
            return u.pathname || null;
        } catch {
            return null;
        }
    }

    selectDocTypeAndGo(type: 'achat' | 'vente') {
        this.selectedDocType = type;
        if (type === 'achat') {
            this.router.navigate(['/main/parametre/document/achat']);
        } else {
            this.router.navigate(['/main/parametre/document/vente']);
        }
    }

    goToDocumentAchat() {
        this.selectedDocType = 'achat';
        this.router.navigate(['/main/parametre/document/achat']);
    }

    goToDocumentVente() {
        this.selectedDocType = 'vente';
        this.router.navigate(['/main/parametre/document/vente']);
    }
}
